import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { Group } from '../domain/group';
import type { Invitation, RestrictedInvitationGroup } from '../domain/invitation';

import { type AppSelector, UserRole, hasAccessToFeature } from '@/common/application/contextSlice';

interface pGroup extends Group { admingroup?: Group } 

export type { pGroup };

const initialState: pGroup = {
    defaultDuration: 0,
    groupConfig: {
        groupConfig: [],
        userGroup: {
            name: '',
        }
    },
    guests: [],
    members: [],
    name: '',
    packets: [],
    prefix: ''
};

const groupSlice = createSlice({
    initialState,
    name: 'group',
    reducers: {
        clearGroup() {
            return initialState;
        },
        removeGuest(state, action: PayloadAction<number>) {
            const guests = state.guests?.slice();
            guests?.splice(action.payload, 1);
            return {
                ...state,
                guests
            };
        },
        removeInvitation(state, action: PayloadAction<number>) {
            const invitations = (state.invitations as Invitation[])?.slice();
            invitations?.splice(action.payload, 1);
            return {
                ...state,
                invitations
            };
        },
        removeMember(state, action: PayloadAction<number>) {
            const members = state.members?.slice();
            members?.splice(action.payload, 1);
            return {
                ...state,
                members
            };
        },
        removeMemberAdmin(state, action: PayloadAction<number>) {
            const members = state.admingroup?.members?.slice();
            members?.splice(action.payload, 1);
            return {
                ...state,
                admingroup: {
                    ...state.admingroup,
                    members: members
                }
            };
        },
        setCurrentAdminGroup(state, action: PayloadAction<Group>) {
            return { ...state, admingroup: action.payload };
        },
        setGroup(state, action: PayloadAction<Group>) {
            const group = action.payload;
            state = { ...state, ...group };
            return state;
        },

        setRestrictedGroup(state, action: PayloadAction<RestrictedInvitationGroup | undefined>) {
            if (action.payload) {
                return {
                    ...state,
                    id: action.payload.id,
                    name: action.payload.name,
                    prefix: action.payload.prefix
                };
            }
            return action.payload;
        },
    },
});

export const { setGroup, clearGroup, removeMember, removeMemberAdmin, removeGuest, removeInvitation, setRestrictedGroup, setCurrentAdminGroup } = groupSlice.actions;

export default groupSlice.reducer;

export const hasAdminRoleInThisGroup = (groupId: string): AppSelector<boolean> => (state) => {

    const userRef = state.auth.user.ref;

    return (() => {
        if (hasAccessToFeature(UserRole.LocalAdmin)(state)) {
            return true;
        }
        if (!userRef) {
            return false;
        }

        let group = state.groups.map[groupId]; // Assuming you have a groups slice with a map of group IDs to group objects

        if (!group || !group.documentId && groupId == state.group?.documentId) {
            group = state.group; // Handle case where the group is not found
        }

        if (!group?.documentId) {
            return false; // Handle case where the group is not found
        }

        
        // Return grouo ownership
        const admins = state.groups.adminsLibrary[groupId];


        if (!admins) {
            return false; // Handle case where the group has no admin group
        }

        return admins.members?.some(member => member.user === userRef) ?? false;
    })();

};

export const hasAdminRoleInTheseGroups = (): AppSelector<{ [key: string]: boolean }> => (state) => {
    const map: { [key: string]: boolean } = {};
    state.groups?.list?.forEach(group => typeof group.documentId === 'undefined' ? null : map[group.documentId] = group.members?.some(member => member.user === state.auth.user.ref) ?? false);
    return map;
};